import React, { useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Image,
  useColorScheme,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const ChallengesScreen = ({ navigation }) => {
  const isDarkMode = useColorScheme() === 'dark';
  const [activeTab, setActiveTab] = useState('official');

  // 当前用户正在进行的挑战
  const currentChallenges = [
    {
      id: 'c1',
      title: '30天低糖饮食',
      description: '30天内控制每日糖分摄入不超过25克',
      category: 'diet',
      progress: 70,
      daysLeft: 9,
      participants: 128,
      friends: [
        'https://randomuser.me/api/portraits/men/32.jpg',
        'https://randomuser.me/api/portraits/women/44.jpg',
        'https://randomuser.me/api/portraits/men/62.jpg',
      ],
      isOfficial: true,
    },
    {
      id: 'c2',
      title: '每日10000步',
      description: '连续21天每天达成10000步行走目标',
      category: 'exercise',
      progress: 42,
      daysLeft: 12,
      participants: 256,
      friends: [
        'https://randomuser.me/api/portraits/women/22.jpg',
        'https://randomuser.me/api/portraits/men/29.jpg',
      ],
      isOfficial: true,
    },
    {
      id: 'c3',
      title: '高蛋白早餐打卡',
      description: '由张健康发起的好友挑战，连续14天摄入高蛋白早餐',
      category: 'diet',
      progress: 35,
      daysLeft: 9,
      participants: 4,
      friends: [
        'https://randomuser.me/api/portraits/men/32.jpg',
        'https://randomuser.me/api/portraits/women/14.jpg',
        'https://randomuser.me/api/portraits/men/11.jpg',
      ],
      isOfficial: false,
      creator: {
        name: '张健康',
        avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
      },
    },
  ];

  // 系统预设挑战
  const officialChallenges = [
    {
      id: 'o1',
      title: '14天地中海饮食',
      description: '体验健康的地中海饮食，摄入更多橄榄油、坚果和鱼类',
      category: 'diet',
      difficulty: 'medium',
      duration: '14天',
      participants: 342,
      reward: {
        points: 200,
        badge: '地中海饮食专家',
      },
    },
    {
      id: 'o2',
      title: '21天喝水习惯养成',
      description: '每天饮水2000ml以上，培养良好饮水习惯',
      category: 'habit',
      difficulty: 'easy',
      duration: '21天',
      participants: 528,
      reward: {
        points: 150,
        badge: '水分补充专家',
      },
    },
    {
      id: 'o3',
      title: '30天每日瑜伽',
      description: '每天进行15分钟以上的瑜伽练习，提升身体柔韧性',
      category: 'exercise',
      difficulty: 'medium',
      duration: '30天',
      participants: 186,
      reward: {
        points: 300,
        badge: '瑜伽爱好者',
      },
    },
    {
      id: 'o4',
      title: '7天蔬果摄入挑战',
      description: '每天摄入5份以上的蔬菜和水果',
      category: 'diet',
      difficulty: 'easy',
      duration: '7天',
      participants: 426,
      reward: {
        points: 100,
        badge: '蔬果爱好者',
      },
    },
  ];

  // 好友发起的挑战
  const friendChallenges = [
    {
      id: 'f1',
      title: '一周无油炸食品',
      description: '由李营养发起的挑战，一周内不食用任何油炸食品',
      category: 'diet',
      difficulty: 'medium',
      duration: '7天',
      participants: 3,
      creator: {
        name: '李营养',
        avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
      },
      friends: [
        'https://randomuser.me/api/portraits/women/44.jpg',
        'https://randomuser.me/api/portraits/men/62.jpg',
        'https://randomuser.me/api/portraits/women/22.jpg',
      ],
    },
    {
      id: 'f2',
      title: '每日健康打卡',
      description: '由跑步达人发起的挑战，每天记录饮食和运动数据',
      category: 'habit',
      difficulty: 'easy',
      duration: '14天',
      participants: 5,
      creator: {
        name: '跑步达人',
        avatar: 'https://randomuser.me/api/portraits/men/62.jpg',
      },
      friends: [
        'https://randomuser.me/api/portraits/men/62.jpg',
        'https://randomuser.me/api/portraits/men/32.jpg',
        'https://randomuser.me/api/portraits/women/44.jpg',
        'https://randomuser.me/api/portraits/women/22.jpg',
        'https://randomuser.me/api/portraits/men/29.jpg',
      ],
    },
  ];

  // 获取挑战类别图标
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'diet':
        return <Ionicons name="nutrition" size={18} color="#ec4899" />;
      case 'exercise':
        return <Ionicons name="fitness" size={18} color="#2563eb" />;
      case 'habit':
        return <Ionicons name="calendar" size={18} color="#8b5cf6" />;
      default:
        return <Ionicons name="trophy" size={18} color="#f59e0b" />;
    }
  };

  // 获取难度级别样式
  const getDifficultyStyle = (difficulty) => {
    switch (difficulty) {
      case 'easy':
        return { color: '#10b981', backgroundColor: '#10b98120' };
      case 'medium':
        return { color: '#f59e0b', backgroundColor: '#f59e0b20' };
      case 'hard':
        return { color: '#ef4444', backgroundColor: '#ef444420' };
      default:
        return { color: '#6b7280', backgroundColor: '#6b728020' };
    }
  };

  // 渲染正在进行的挑战
  const renderCurrentChallenges = () => {
    return (
      <View style={{ marginBottom: 24 }}>
        <Text style={{
          fontSize: 18,
          fontWeight: 'bold',
          marginBottom: 12,
          color: isDarkMode ? 'white' : '#1f2937',
        }}>
          正在进行的挑战
        </Text>

        {currentChallenges.map((challenge) => (
          <TouchableOpacity
            key={challenge.id}
            style={{
              backgroundColor: isDarkMode ? '#374151' : 'white',
              borderRadius: 16,
              padding: 16,
              marginBottom: 16,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 1 },
              shadowOpacity: 0.1,
              shadowRadius: 2,
              elevation: 2,
            }}
            onPress={() => navigation.navigate('ChallengeDetail', { challengeId: challenge.id })}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
              <View style={{
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: isDarkMode ? '#4b5563' : '#f3f4f6',
                paddingHorizontal: 8,
                paddingVertical: 4,
                borderRadius: 12,
                marginRight: 8,
              }}>
                {getCategoryIcon(challenge.category)}
                <Text style={{
                  fontSize: 12,
                  marginLeft: 4,
                  color: isDarkMode ? '#d1d5db' : '#6b7280',
                }}>
                  {challenge.category === 'diet' ? '饮食' :
                    challenge.category === 'exercise' ? '运动' : '习惯'}
                </Text>
              </View>

              {challenge.isOfficial && (
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  backgroundColor: isDarkMode ? '#4b5563' : '#e0f2fe',
                  paddingHorizontal: 8,
                  paddingVertical: 4,
                  borderRadius: 12,
                }}>
                  <Ionicons name="shield-checkmark" size={12} color="#2563eb" />
                  <Text style={{
                    fontSize: 12,
                    marginLeft: 4,
                    color: '#2563eb',
                  }}>
                    官方
                  </Text>
                </View>
              )}
            </View>

            <Text style={{
              fontSize: 18,
              fontWeight: 'bold',
              color: isDarkMode ? 'white' : '#1f2937',
              marginBottom: 4,
            }}>
              {challenge.title}
            </Text>

            <Text style={{
              fontSize: 14,
              color: isDarkMode ? '#d1d5db' : '#6b7280',
              marginBottom: 12,
            }}>
              {challenge.description}
            </Text>

            {/* 进度条 */}
            <View style={{ marginBottom: 12 }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
                <Text style={{ fontSize: 12, color: isDarkMode ? '#d1d5db' : '#6b7280' }}>
                  进度: {challenge.progress}%
                </Text>
                <Text style={{ fontSize: 12, color: isDarkMode ? '#d1d5db' : '#6b7280' }}>
                  剩余 {challenge.daysLeft} 天
                </Text>
              </View>
              <View style={{
                height: 8,
                backgroundColor: isDarkMode ? '#4b5563' : '#f3f4f6',
                borderRadius: 4,
                overflow: 'hidden',
              }}>
                <View style={{
                  width: `${challenge.progress}%`,
                  height: '100%',
                  backgroundColor: '#2563eb',
                  borderRadius: 4,
                }} />
              </View>
            </View>

            {/* 参与者信息 */}
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <View style={{ flexDirection: 'row' }}>
                {challenge.friends.map((avatar, index) => (
                  <Image
                    key={index}
                    source={{ uri: avatar }}
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 13,
                      marginLeft: index > 0 ? -10 : 0,
                      borderWidth: 2,
                      borderColor: isDarkMode ? '#374151' : 'white',
                    }}
                  />
                ))}
              </View>
              <Text style={{
                marginLeft: 8,
                fontSize: 12,
                color: isDarkMode ? '#d1d5db' : '#6b7280',
              }}>
                {challenge.participants} 人参与
              </Text>
            </View>
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  // 渲染官方挑战
  const renderOfficialChallenges = () => {
    return (
      <ScrollView>
        {officialChallenges.map((challenge) => (
          <TouchableOpacity
            key={challenge.id}
            style={{
              backgroundColor: isDarkMode ? '#374151' : 'white',
              borderRadius: 16,
              padding: 16,
              marginBottom: 16,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 1 },
              shadowOpacity: 0.1,
              shadowRadius: 2,
              elevation: 2,
            }}
            onPress={() => navigation.navigate('ChallengeDetail', { challengeId: challenge.id })}
          >
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
              <View style={{
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: isDarkMode ? '#4b5563' : '#f3f4f6',
                paddingHorizontal: 8,
                paddingVertical: 4,
                borderRadius: 12,
              }}>
                {getCategoryIcon(challenge.category)}
                <Text style={{
                  fontSize: 12,
                  marginLeft: 4,
                  color: isDarkMode ? '#d1d5db' : '#6b7280',
                }}>
                  {challenge.category === 'diet' ? '饮食' :
                    challenge.category === 'exercise' ? '运动' : '习惯'}
                </Text>
              </View>

              <View style={{
                paddingHorizontal: 8,
                paddingVertical: 4,
                borderRadius: 12,
                backgroundColor: getDifficultyStyle(challenge.difficulty).backgroundColor,
              }}>
                <Text style={{
                  fontSize: 12,
                  color: getDifficultyStyle(challenge.difficulty).color,
                }}>
                  {challenge.difficulty === 'easy' ? '简单' :
                    challenge.difficulty === 'medium' ? '中等' : '困难'}
                </Text>
              </View>
            </View>

            <Text style={{
              fontSize: 18,
              fontWeight: 'bold',
              color: isDarkMode ? 'white' : '#1f2937',
              marginBottom: 4,
            }}>
              {challenge.title}
            </Text>

            <Text style={{
              fontSize: 14,
              color: isDarkMode ? '#d1d5db' : '#6b7280',
              marginBottom: 12,
            }}>
              {challenge.description}
            </Text>

            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 12 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Ionicons name="time-outline" size={16} color={isDarkMode ? '#d1d5db' : '#6b7280'} style={{ marginRight: 4 }} />
                <Text style={{ fontSize: 14, color: isDarkMode ? '#d1d5db' : '#6b7280' }}>
                  {challenge.duration}
                </Text>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Ionicons name="people-outline" size={16} color={isDarkMode ? '#d1d5db' : '#6b7280'} style={{ marginRight: 4 }} />
                <Text style={{ fontSize: 14, color: isDarkMode ? '#d1d5db' : '#6b7280' }}>
                  {challenge.participants} 人参与
                </Text>
              </View>
            </View>

            {/* 奖励信息 */}
            <View style={{
              flexDirection: 'row',
              backgroundColor: isDarkMode ? '#4b5563' : '#f0f9ff',
              borderRadius: 12,
              padding: 12,
            }}>
              <View style={{
                width: 40,
                height: 40,
                borderRadius: 20,
                backgroundColor: '#2563eb',
                justifyContent: 'center',
                alignItems: 'center',
                marginRight: 12,
              }}>
                <Ionicons name="ribbon" size={20} color="white" />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{
                  fontSize: 14,
                  fontWeight: 'bold',
                  color: isDarkMode ? 'white' : '#1f2937',
                  marginBottom: 2,
                }}>
                  完成奖励
                </Text>
                <Text style={{ fontSize: 13, color: isDarkMode ? '#d1d5db' : '#6b7280' }}>
                  获得 {challenge.reward.points} 积分和"{challenge.reward.badge}"徽章
                </Text>
              </View>
            </View>
          </TouchableOpacity>
        ))}
      </ScrollView>
    );
  };

  // 渲染好友挑战
  const renderFriendChallenges = () => {
    return (
      <ScrollView>
        {friendChallenges.map((challenge) => (
          <TouchableOpacity
            key={challenge.id}
            style={{
              backgroundColor: isDarkMode ? '#374151' : 'white',
              borderRadius: 16,
              padding: 16,
              marginBottom: 16,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 1 },
              shadowOpacity: 0.1,
              shadowRadius: 2,
              elevation: 2,
            }}
            onPress={() => navigation.navigate('ChallengeDetail', { challengeId: challenge.id })}
          >
            {/* 创建者信息 */}
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
              <Image
                source={{ uri: challenge.creator.avatar }}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  marginRight: 8,
                }}
              />
              <View>
                <Text style={{ fontWeight: 'bold', color: isDarkMode ? 'white' : '#1f2937' }}>
                  {challenge.creator.name}
                </Text>
                <Text style={{ fontSize: 12, color: isDarkMode ? '#d1d5db' : '#6b7280' }}>
                  发起的挑战
                </Text>
              </View>
            </View>

            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
              <View style={{
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: isDarkMode ? '#4b5563' : '#f3f4f6',
                paddingHorizontal: 8,
                paddingVertical: 4,
                borderRadius: 12,
              }}>
                {getCategoryIcon(challenge.category)}
                <Text style={{
                  fontSize: 12,
                  marginLeft: 4,
                  color: isDarkMode ? '#d1d5db' : '#6b7280',
                }}>
                  {challenge.category === 'diet' ? '饮食' :
                    challenge.category === 'exercise' ? '运动' : '习惯'}
                </Text>
              </View>

              <View style={{
                paddingHorizontal: 8,
                paddingVertical: 4,
                borderRadius: 12,
                backgroundColor: getDifficultyStyle(challenge.difficulty).backgroundColor,
              }}>
                <Text style={{
                  fontSize: 12,
                  color: getDifficultyStyle(challenge.difficulty).color,
                }}>
                  {challenge.difficulty === 'easy' ? '简单' :
                    challenge.difficulty === 'medium' ? '中等' : '困难'}
                </Text>
              </View>
            </View>

            <Text style={{
              fontSize: 18,
              fontWeight: 'bold',
              color: isDarkMode ? 'white' : '#1f2937',
              marginBottom: 4,
            }}>
              {challenge.title}
            </Text>

            <Text style={{
              fontSize: 14,
              color: isDarkMode ? '#d1d5db' : '#6b7280',
              marginBottom: 12,
            }}>
              {challenge.description}
            </Text>

            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 12 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Ionicons name="time-outline" size={16} color={isDarkMode ? '#d1d5db' : '#6b7280'} style={{ marginRight: 4 }} />
                <Text style={{ fontSize: 14, color: isDarkMode ? '#d1d5db' : '#6b7280' }}>
                  {challenge.duration}
                </Text>
              </View>
            </View>

            {/* 参与好友 */}
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Text style={{ 
                fontSize: 14, 
                color: isDarkMode ? '#d1d5db' : '#6b7280',
                marginRight: 8,
              }}>
                参与好友:
              </Text>
              <View style={{ flexDirection: 'row' }}>
                {challenge.friends.map((avatar, index) => (
                  <Image
                    key={index}
                    source={{ uri: avatar }}
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 13,
                      marginLeft: index > 0 ? -10 : 0,
                      borderWidth: 2,
                      borderColor: isDarkMode ? '#374151' : 'white',
                    }}
                  />
                ))}
              </View>
            </View>
          </TouchableOpacity>
        ))}

        <TouchableOpacity 
          style={{ 
            backgroundColor: isDarkMode ? '#374151' : '#f0f9ff',
            borderRadius: 16,
            padding: 16,
            marginBottom: 16,
            borderWidth: 1,
            borderColor: '#2563eb',
            borderStyle: 'dashed',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onPress={() => navigation.navigate('CreateChallenge')}
        >
          <View style={{ 
            width: 48,
            height: 48,
            borderRadius: 24,
            backgroundColor: '#2563eb20',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 8,
          }}>
            <Ionicons name="add" size={28} color="#2563eb" />
          </View>
          <Text style={{ 
            fontSize: 16,
            fontWeight: 'bold',
            color: '#2563eb',
            marginBottom: 4,
          }}>
            创建自定义挑战
          </Text>
          <Text style={{ 
            fontSize: 14,
            color: isDarkMode ? '#d1d5db' : '#6b7280',
            textAlign: 'center',
          }}>
            自定义挑战内容，邀请好友一起参与
          </Text>
        </TouchableOpacity>
      </ScrollView>
    );
  };

  return (
    <View style={{
      flex: 1,
      backgroundColor: isDarkMode ? '#1f2937' : '#f9fafb',
    }}>
      <View style={{ padding: 16 }}>
        {/* 顶部导航标签 */}
        <View style={{
          flexDirection: 'row',
          backgroundColor: isDarkMode ? '#374151' : 'white',
          borderRadius: 12,
          padding: 4,
          marginBottom: 16,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.1,
          shadowRadius: 2,
          elevation: 2,
        }}>
          <TouchableOpacity
            style={{
              flex: 1,
              paddingVertical: 10,
              alignItems: 'center',
              borderRadius: 10,
              backgroundColor: activeTab === 'current'
                ? (isDarkMode ? '#4b5563' : '#e0f2fe')
                : 'transparent',
            }}
            onPress={() => setActiveTab('current')}
          >
            <Text style={{
              color: activeTab === 'current' ? '#2563eb' : (isDarkMode ? '#d1d5db' : '#6b7280'),
              fontWeight: activeTab === 'current' ? 'bold' : 'normal',
            }}>
              进行中
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={{
              flex: 1,
              paddingVertical: 10,
              alignItems: 'center',
              borderRadius: 10,
              backgroundColor: activeTab === 'official'
                ? (isDarkMode ? '#4b5563' : '#e0f2fe')
                : 'transparent',
            }}
            onPress={() => setActiveTab('official')}
          >
            <Text style={{
              color: activeTab === 'official' ? '#2563eb' : (isDarkMode ? '#d1d5db' : '#6b7280'),
              fontWeight: activeTab === 'official' ? 'bold' : 'normal',
            }}>
              官方挑战
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={{
              flex: 1,
              paddingVertical: 10,
              alignItems: 'center',
              borderRadius: 10,
              backgroundColor: activeTab === 'friends'
                ? (isDarkMode ? '#4b5563' : '#e0f2fe')
                : 'transparent',
            }}
            onPress={() => setActiveTab('friends')}
          >
            <Text style={{
              color: activeTab === 'friends' ? '#2563eb' : (isDarkMode ? '#d1d5db' : '#6b7280'),
              fontWeight: activeTab === 'friends' ? 'bold' : 'normal',
            }}>
              好友挑战
            </Text>
          </TouchableOpacity>
        </View>

        {/* 当前选中的标签内容 */}
        {activeTab === 'current' && renderCurrentChallenges()}
        {activeTab === 'official' && renderOfficialChallenges()}
        {activeTab === 'friends' && renderFriendChallenges()}
      </View>
    </View>
  );
};

export default ChallengesScreen; 